import React, { useEffect, useState } from "react";
import {
  Table,
  Typography,
  Row,
  Col,
  Select,
  Form,
  notification,
  DatePicker,
  Card,
  Button,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import PieChart from "./charts/pieChart";
import {
  getStockDetailsService,
  exportRetailPossibilityMtdReportService,
} from "../../../../services/dashboard-services";
import { getAreasInStockService } from "../../../../services/master-services";
import Loader from "../../../../components/common/Loader";

const { Title } = Typography;

const DashboardStock = () => {
  const navigate = useNavigate();
  const isAdmin = useSelector((state) => state?.auth?.isAdmin);
  const roleType = useSelector((state) => state?.auth?.userData?.role);
  const route = isAdmin ? "/admin" : "";
  const isExchangeLeadUser =
    roleType === "Exchange Evaluation Team" ||
    roleType === "Procurement Manager";
  const exchangeRoute = isExchangeLeadUser ? "/exchange-lead" : "";
  const [selectedPhysicalLocations, setSelectedPhysicalLocations] = useState(
    []
  );
  const [areaData, setAreaData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [physicalStockData, setPhysicalStockData] = useState([]);
  const [unmatchedStockData, setUnmatchedStockData] = useState([]);
  const [cancellationReportData, setCancellationReportData] = useState([
    ["Reason", "Count"],
  ]);
  const [cancellationChartColors, setCancellationChartColors] = useState([
    "#4285f4",
    "#ea4335",
    "#fbbc04",
    "#34a853",
  ]);
  const [refundTatData, setRefundTatData] = useState([]);
  const [liveBookingData, setLiveBookingData] = useState([]);
  const [liveBookingColumns, setLiveBookingColumns] = useState([]);
  const [mtdBookingData, setMtdBookingData] = useState([]);
  const [mtdBookingColumns, setMtdBookingColumns] = useState([]);
  const [matchedStockData, setMatchedStockData] = useState([]);
  const [matchedStockColumns, setMatchedStockColumns] = useState([]);
  const [retailPossibilityData, setRetailPossibilityData] = useState([]);
  const [retailPossibilityColumns, setRetailPossibilityColumns] = useState([]);
  const [isRetailExporting, setIsRetailExporting] = useState(false);
  const currentMonthStart = dayjs().startOf("month");
  const currentDate = dayjs();
  const [dates, setDates] = useState({
    startDate: currentMonthStart,
    endDate: currentDate,
  });

  // Fetch area data for dropdown
  const getAreaData = async () => {
    try {
      const areaDataArray = await getAreasInStockService(true);
      setAreaData(areaDataArray?.data?.data || []);
    } catch (error) {
      notification.error({
        description: error.response?.data?.message ?? error?.message,
      });
    }
  };

  const getRetailPossibilityStockCategory = (matching) => {
    const retailCategory = String(matching || "").toLowerCase();
    if (retailCategory === "delivered") return "retail_possibility_delivered";
    if (retailCategory === "matched") return "retail_possibility_matched";
    return retailCategory;
  };

  const renderCountLink = (value, onClick) => {
    const num = Number(value);
    if (Number.isNaN(num) || num <= 0) return value ?? 0;
    return (
      <span
        style={{ color: "#1677ff", cursor: "pointer" }}
        onClick={onClick}
      >
        {value}
      </span>
    );
  };

  const STOCK_REDIRECT_WITHOUT_MODEL = new Set([
    "unmatched_mddp_stock_total",
    "unmatched_physical_stock_total",
    "unmatched_transit_stock_total",
    "unmatched_summary_total",
    "mddp_stock",
    "physical_stock",
    "transit_stock",
  ]);

  const renderStockCountCell = (text, record, category) => {
    if (record.isTotalRow) {
      return <strong>{text}</strong>;
    }
    return renderCountLink(text, () => handleStockRedirect(record, category));
  };

  // Helper function to format price in crores (C notation)
  const formatPriceInCrores = (price) => {
    if (!price || price === 0) return "-";
    const crores = price / 10000000; // Convert to crores (1 crore = 10,000,000)
    return `${crores.toFixed(2)}C`;
  };

  const fetchStockDetails = async (
    physical_location_ids = selectedPhysicalLocations,
    dateRange = dates
  ) => {
    try {
      setLoading(true);
      const start_date = dateRange?.startDate ? dateRange.startDate.format("YYYY-MM-DD") : "";
      const end_date = dateRange?.endDate ? dateRange.endDate.format("YYYY-MM-DD") : "";
      const response = await getStockDetailsService(
        physical_location_ids,
        start_date,
        end_date
      );
      console.log("Stock Details Response:", response);

      // Extract physical_stock_analysis_table from response
      const physicalStockAnalysisTable =
        response?.data?.data?.physical_stock_analysis_table || [];

      // Transform API data to table format
      const transformedData = physicalStockAnalysisTable.map((item, index) => {
        // Check if this is the Total row
        const isTotalRow = item.model_id === "" || item.model_name === "Total";

        return {
          key: isTotalRow ? "total" : `model-${item.model_id || index}`,
          model: isTotalRow ? "Grand Count (Total)" : item.model_name || "-",
          modelId: item.model_id || null,
          mddpStock: item.mddp_stock || 0,
          physicalStock: item.physical_stock || 0,
          transitStock: item.transit_stock || 0,
          isTotalRow: isTotalRow,
        };
      });

      setPhysicalStockData(transformedData);

      // Extract unmatched_stock_analysis_report from response
      const unmatchedStockAnalysisReport =
        response?.data?.data?.unmatched_stock_analysis_report || [];

      // Transform unmatched stock data to table format
      const transformedUnmatchedData = unmatchedStockAnalysisReport.map(
        (item, index) => {
          const isUnmatchedTotalRow = item.model_name === "Total";
          const mddpTotal = item.mddp_stock_total || 0;
          const mddpPrice = item.mddp_stock_price || 0;
          const physicalTotal = item.physical_stock_total || 0;
          const physicalPrice = item.physical_stock_price || 0;
          const transitTotal = item.transit_stock_total || 0;
          const transitPrice = item.transit_stock_price || 0;

          // Calculate summary totals and prices
          const summaryTotal = mddpTotal + physicalTotal + transitTotal;
          const summaryPrice = mddpPrice + physicalPrice + transitPrice;

          return {
            key: `unmatched-${item.model_id || index}`,
            model: item.model_name || "-",
            modelId: item.model_id || null,
            mddpTotal: mddpTotal,
            mddpPrice: formatPriceInCrores(mddpPrice),
            physicalTotal: physicalTotal,
            physicalPrice: formatPriceInCrores(physicalPrice),
            transitTotal: transitTotal,
            transitPrice: formatPriceInCrores(transitPrice),
            summaryTotal: summaryTotal,
            summaryPrice: formatPriceInCrores(summaryPrice),
            isUnmatchedTotalRow,
          };
        }
      );

      // Calculate Grand Total row (exclude API "Total" row to avoid double-counting)
      const unmatchedRowsForGrandTotal = unmatchedStockAnalysisReport.filter(
        (item) => item.model_name !== "Total"
      );
      if (unmatchedRowsForGrandTotal.length > 0) {
        const grandTotal = unmatchedRowsForGrandTotal.reduce(
          (acc, item) => ({
            mddpTotal: acc.mddpTotal + (item.mddp_stock_total || 0),
            mddpPrice: acc.mddpPrice + (item.mddp_stock_price || 0),
            physicalTotal: acc.physicalTotal + (item.physical_stock_total || 0),
            physicalPrice: acc.physicalPrice + (item.physical_stock_price || 0),
            transitTotal: acc.transitTotal + (item.transit_stock_total || 0),
            transitPrice: acc.transitPrice + (item.transit_stock_price || 0),
            summaryTotal:
              acc.summaryTotal +
              ((item.mddp_stock_total || 0) +
                (item.physical_stock_total || 0) +
                (item.transit_stock_total || 0)),
            summaryPrice:
              acc.summaryPrice +
              ((item.mddp_stock_price || 0) +
                (item.physical_stock_price || 0) +
                (item.transit_stock_price || 0)),
          }),
          {
            mddpTotal: 0,
            mddpPrice: 0,
            physicalTotal: 0,
            physicalPrice: 0,
            transitTotal: 0,
            transitPrice: 0,
            summaryTotal: 0,
            summaryPrice: 0,
          }
        );

        transformedUnmatchedData.push({
          key: "unmatched-total",
          model: "Total (Grand Total)",
          mddpTotal: grandTotal.mddpTotal,
          mddpPrice: formatPriceInCrores(grandTotal.mddpPrice),
          physicalTotal: grandTotal.physicalTotal,
          physicalPrice: formatPriceInCrores(grandTotal.physicalPrice),
          transitTotal: grandTotal.transitTotal,
          transitPrice: formatPriceInCrores(grandTotal.transitPrice),
          summaryTotal: grandTotal.summaryTotal,
          summaryPrice: formatPriceInCrores(grandTotal.summaryPrice),
          isTotalRow: true,
        });
      }

      setUnmatchedStockData(transformedUnmatchedData);

      // Extract cancel_delivery_report from response
      const cancelDeliveryReport =
        response?.data?.data?.cancel_delivery_report || {};

      // Helper function to convert snake_case to Title Case
      const snakeToTitleCase = (str) => {
        return str
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(" ");
      };

      // Transform cancellation report data to pie chart format dynamically
      const cancellationChartData = [["Reason", "Count"]];

      // Get all keys from the cancel_delivery_report object and create chart data
      const reasonEntries = [];
      Object.keys(cancelDeliveryReport).forEach((key) => {
        const value = cancelDeliveryReport[key];
        // Include only non-zero numeric values to show meaningful data
        // This ensures all slices are visible, even small ones (like value 1)
        if (typeof value === "number" && !isNaN(value) && value > 0) {
          const label = snakeToTitleCase(key);
          reasonEntries.push([label, value]);
        }
      });

      // Sort by count (descending) so most common reasons appear first
      reasonEntries.sort((a, b) => b[1] - a[1]);

      // Add sorted entries to chart data
      cancellationChartData.push(...reasonEntries);

      setCancellationReportData(cancellationChartData);

      // Generate dynamic colors for the pie chart based on number of reasons
      const reasonCount = reasonEntries.length;
      const baseColors = ["#4285f4", "#ea4335", "#fbbc04", "#34a853", "#ff6d00", "#9c27b0", "#00bcd4", "#8bc34a", "#ff9800", "#795548"];
      const dynamicColors = [];
      for (let i = 0; i < reasonCount; i++) {
        dynamicColors.push(baseColors[i % baseColors.length]);
      }
      setCancellationChartColors(dynamicColors);

      // Extract refund_tat_report from response
      const refundTatReport = response?.data?.data?.refund_tat_report || [];

      // Transform refund TAT report data to table format
      const transformedRefundTatData = refundTatReport.map((item, index) => {
        return {
          key: `refund-tat-${item.lead_id || index}`,
          leadId: item.lead_id || "-",
          customerName: item.customer_name || "-",
          area: item.customer_area || "-",
          cancellationRequestCreationDate:
            item.cancel_request_created_at || "-",
          cancellationRequestApprovalDate:
            item.cancel_request_approved_at || "-",
          refundTransactionEntryDate: item.refund_transaction_entry_date || "-",
          totalTat:
            item.total_tat_days !== null &&
              item.total_tat_days !== undefined &&
              item.total_tat_days !== ""
              ? item.total_tat_days
              : "-",
        };
      });

      setRefundTatData(transformedRefundTatData);

      // Extract live_booking_report from response
      const liveBookingReport = response?.data?.data?.live_booking_report || {};
      const columns = liveBookingReport?.columns || [];
      const categories = liveBookingReport?.categories || [];
      const grandCount = liveBookingReport?.grand_count || {};

      // Generate dynamic columns for LIVE BOOKING table
      const generatedColumns = [
        {
          title: "Row Labels",
          dataIndex: "rowLabels",
          key: "rowLabels",
          fixed: "left",
          width: 180,
          render: (text, record) => {
            if (record.isTotalRow || record.isCategoryRow) {
              return <strong>{text}</strong>;
            }
            return text;
          },
        },
        ...columns.map((col, index) => ({
          title: col.name,
          dataIndex: `area_${index}`,
          key: `area_${index}`,
          align: "center",
          width: 100,
          render: (text, record) => {
            if (record.isTotalRow || record.isCategoryRow) {
              return <strong>{text}</strong>;
            }
            if (!record.modelId) return text ?? 0;
            const areaId = col.id || col.area_id;
            return renderCountLink(text, () =>
              handleLiveBookingRedirect(record, areaId, col.name)
            );
          },
        })),
        {
          title: "Total",
          dataIndex: "total",
          key: "total",
          align: "center",
          fixed: "right",
          width: 100,
          render: (text, record) => {
            if (record.isTotalRow || record.isCategoryRow) {
              return <strong>{text}</strong>;
            }
            if (!record.modelId) return text ?? 0;
            return renderCountLink(text, () => handleLiveBookingRedirect(record));
          },
        },
      ];
      setLiveBookingColumns(generatedColumns);

      // Transform live booking data to table format
      const transformedLiveBookingData = [];
      let rowKeyCounter = 1;

      categories.forEach((category) => {
        // Add model rows within category FIRST
        category.rows?.forEach((row) => {
          transformedLiveBookingData.push({
            key: `row-${rowKeyCounter++}`,
            rowLabels: row.row_label || row.model_name || "-",
            modelId: row.model_id || row.model_type_id || null,
            liveBookingCategory: (category.category || "").toLowerCase(),
            ...columns.reduce((acc, col, index) => {
              acc[`area_${index}`] = row.counts?.[`area_${index}`] || 0;
              return acc;
            }, {}),
            total: row.total || 0,
            isCategoryRow: false,
          });
        });

        // Add category header row (Matched/Unmatched) AFTER model rows
        transformedLiveBookingData.push({
          key: `category-${rowKeyCounter++}`,
          rowLabels: category.category,
          ...columns.reduce((acc, col, index) => {
            acc[`area_${index}`] =
              category.total_row?.counts?.[`area_${index}`] || 0;
            return acc;
          }, {}),
          total: category.total_row?.total || 0,
          isCategoryRow: true,
        });
      });

      // Add Grand Count row
      if (grandCount && Object.keys(grandCount).length > 0) {
        transformedLiveBookingData.push({
          key: `grand-count-${rowKeyCounter++}`,
          rowLabels: grandCount.row_label || "Grand Count",
          ...columns.reduce((acc, col, index) => {
            acc[`area_${index}`] = grandCount.counts?.[`area_${index}`] || 0;
            return acc;
          }, {}),
          total: grandCount.total || 0,
          isTotalRow: true,
        });
      }

      setLiveBookingData(transformedLiveBookingData);

      // Extract mtd_book_report from response
      const mtdBookReport = response?.data?.data?.mtd_book_report || {};
      const mtdColumns = mtdBookReport?.columns || [];
      const mtdRows = mtdBookReport?.rows || [];
      const mtdTotalRow = mtdBookReport?.total_row || {};
      const mtdGrandCount = mtdBookReport?.grand_count || {};

      // Generate dynamic columns for MTD Booking table
      const generatedMtdColumns = [
        {
          title: "Model",
          dataIndex: "model",
          key: "model",
          fixed: "left",
          width: 150,
          render: (text, record) => {
            if (record.isTotalRow) {
              return <strong>{text}</strong>;
            }
            return text;
          },
        },
        ...mtdColumns.map((col, index) => ({
          title: col.name,
          dataIndex: `area_${index}`,
          key: `area_${index}`,
          align: "center",
          width: 100,
          render: (text, record) => {
            if (record.isTotalRow) {
              return <strong>{text}</strong>;
            }
            if (!record.modelId) return text ?? 0;
            const areaId = col.id || col.area_id;
            return renderCountLink(text, () =>
              handleMtdBookingRedirect(record, areaId)
            );
          },
        })),
        {
          title: "Total",
          dataIndex: "total",
          key: "total",
          align: "center",
          fixed: "right",
          width: 100,
          render: (text, record) => {
            if (record.isTotalRow) {
              return <strong>{text}</strong>;
            }
            if (!record.modelId) return text ?? 0;
            return renderCountLink(text, () => handleMtdBookingRedirect(record));
          },
        },
      ];
      setMtdBookingColumns(generatedMtdColumns);

      // Transform MTD Booking data to table format
      const transformedMtdBookingData = [];
      let mtdRowKeyCounter = 1;

      // Add model rows
      mtdRows.forEach((row) => {
        transformedMtdBookingData.push({
          key: `mtd-row-${mtdRowKeyCounter++}`,
          model: row.row_label || row.model_name || "-",
          modelId: row.model_id || null,
          ...mtdColumns.reduce((acc, col, index) => {
            acc[`area_${index}`] = row.counts?.[`area_${index}`] || 0;
            return acc;
          }, {}),
          total: row.total || 0,
          isTotalRow: false,
        });
      });

      // Add Total row (from total_row in response)
      if (
        mtdTotalRow &&
        (mtdTotalRow.row_label ||
          mtdTotalRow.model_name ||
          mtdTotalRow.total !== undefined)
      ) {
        transformedMtdBookingData.push({
          key: `mtd-total-${mtdRowKeyCounter++}`,
          model: mtdTotalRow.row_label || mtdTotalRow.model_name || "Total",
          ...mtdColumns.reduce((acc, col, index) => {
            acc[`area_${index}`] = mtdTotalRow.counts?.[`area_${index}`] ?? 0;
            return acc;
          }, {}),
          total: mtdTotalRow.total ?? 0,
          isTotalRow: true,
        });
      }

      // Add Grand Count row (from grand_count in response)
      if (
        mtdGrandCount &&
        (mtdGrandCount.row_label ||
          mtdGrandCount.model_name ||
          mtdGrandCount.total !== undefined)
      ) {
        transformedMtdBookingData.push({
          key: `mtd-grand-${mtdRowKeyCounter++}`,
          model:
            mtdGrandCount.row_label ||
            mtdGrandCount.model_name ||
            "Grand Count",
          ...mtdColumns.reduce((acc, col, index) => {
            acc[`area_${index}`] = mtdGrandCount.counts?.[`area_${index}`] ?? 0;
            return acc;
          }, {}),
          total: mtdGrandCount.total ?? 0,
          isTotalRow: true,
        });
      }

      setMtdBookingData(transformedMtdBookingData);

      // Extract matched_stock_analysis_report from response
      const matchedStockReport =
        response?.data?.data?.matched_stock_analysis_report || {};
      const matchedColumns = matchedStockReport?.columns || [];
      const matchedCategories = matchedStockReport?.categories || [];
      const matchedGrandTotal = matchedStockReport?.grand_total_row || {};

      // Generate dynamic columns for MATCHED STOCK ANALYSIS table
      const generatedMatchedColumns = [
        {
          title: "Stock Status",
          dataIndex: "stockStatus",
          key: "stockStatus",
          fixed: "left",
          width: 150,
          onCell: (record, index) => {
            // Calculate rowSpan for Stock Status column
            if (record.isTotalRow || record.isSubtotalRow) {
              return {};
            }

            const dataSource = transformedMatchedData;
            const currentStockStatus = record.stockStatus;

            // Find how many consecutive rows have the same stock status
            let rowSpan = 1;
            for (let i = index + 1; i < dataSource.length; i++) {
              if (dataSource[i].stockStatus === currentStockStatus &&
                !dataSource[i].isTotalRow &&
                !dataSource[i].isSubtotalRow) {
                rowSpan++;
              } else {
                break;
              }
            }

            // Check if this is the first row of a group
            if (index === 0 || dataSource[index - 1].stockStatus !== currentStockStatus ||
              dataSource[index - 1].isTotalRow || dataSource[index - 1].isSubtotalRow) {
              return { rowSpan };
            }

            // Hide subsequent rows in the same group
            return { rowSpan: 0 };
          },
          render: (text, record) => {
            if (record.isTotalRow || record.isSubtotalRow) {
              return <strong>{text || ""}</strong>;
            }
            return text || "";
          },
        },
        {
          title: "Model",
          dataIndex: "model",
          key: "model",
          fixed: "left",
          width: 150,
          render: (text, record) => {
            if (record.isTotalRow || record.isSubtotalRow) {
              return <strong>{text || ""}</strong>;
            }
            return text || "";
          },
        },
        ...matchedColumns
          .map((col, index) => {
            // Skip the summary column as it will be added separately
            if (col.month_key === "summary") {
              return null;
            }
            return {
              title: col.name,
              children: [
                {
                  title: "Total",
                  dataIndex: `col_${index}_total`,
                  key: `col_${index}_total`,
                  align: "center",
                  width: 100,
                  render: (text, record) => {
                    if (record.isTotalRow || record.isSubtotalRow) {
                      return <strong>{text ?? 0}</strong>;
                    }
                    const value = text ?? 0;
                    const stockStatus = String(record.stockStatus || "").toLowerCase();
                    let matchedCategory = "";
                    if (stockStatus.includes("physical")) {
                      matchedCategory = "matched_physical_stock";
                    } else if (stockStatus.includes("mddp")) {
                      matchedCategory = "matched_mddp_stock";
                    } else if (stockStatus.includes("transit")) {
                      matchedCategory = "matched_transit_stock";
                    }
                    if (!matchedCategory) return value;
                    return renderCountLink(value, () =>
                      handleStockRedirect(record, matchedCategory, {
                        matched_stock_month: col.month_key,
                      })
                    );
                  },
                },
                {
                  title: "Price",
                  dataIndex: `col_${index}_price`,
                  key: `col_${index}_price`,
                  align: "center",
                  width: 100,
                  render: (text, record) => {
                    if (record.isTotalRow || record.isSubtotalRow) {
                      return <strong>{text || "-"}</strong>;
                    }
                    return text || "-";
                  },
                },
              ],
            };
          })
          .filter(Boolean),
        // Add Summary column (the last column with month_key === 'summary')
        (() => {
          const summaryCol = matchedColumns.find(
            (col) => col.month_key === "summary"
          );
          if (summaryCol) {
            const summaryIndex = matchedColumns.findIndex(
              (col) => col.month_key === "summary"
            );
            return {
              title: summaryCol.name,
              children: [
                {
                  title: "Total",
                  dataIndex: `col_${summaryIndex}_total`,
                  key: `col_${summaryIndex}_total`,
                  align: "center",
                  width: 100,
                  render: (text, record) => {
                    if (record.isTotalRow || record.isSubtotalRow) {
                      return <strong>{text ?? 0}</strong>;
                    }
                    const value = text ?? 0;
                    const stockStatus = String(record.stockStatus || "").toLowerCase();
                    let matchedCategory = "";
                    if (stockStatus.includes("physical")) {
                      matchedCategory = "matched_physical_stock";
                    } else if (stockStatus.includes("mddp")) {
                      matchedCategory = "matched_mddp_stock";
                    } else if (stockStatus.includes("transit")) {
                      matchedCategory = "matched_transit_stock";
                    }
                    if (!matchedCategory) return value;
                    return renderCountLink(value, () =>
                      handleStockRedirect(record, matchedCategory, {
                        matched_stock_month: summaryCol.month_key,
                      })
                    );
                  },
                },
                {
                  title: "Price",
                  dataIndex: `col_${summaryIndex}_price`,
                  key: `col_${summaryIndex}_price`,
                  align: "center",
                  width: 100,
                  render: (text, record) => {
                    if (record.isTotalRow || record.isSubtotalRow) {
                      return <strong>{text || "-"}</strong>;
                    }
                    return text || "-";
                  },
                },
              ],
            };
          }
          return null;
        })(),
      ].filter(Boolean);

      setMatchedStockColumns(generatedMatchedColumns);

      // Transform matched stock data to table format
      const transformedMatchedData = [];
      let matchedRowKeyCounter = 1;

      matchedCategories.forEach((category) => {
        // Process all rows in the category
        category.rows?.forEach((row) => {
          // Check if this is a subtotal row (model_id is null and row_label matches category)
          const isSubtotal = row.model_id === null && row.row_label === category.category_label;

          const rowData = {
            key: `matched-row-${matchedRowKeyCounter++}`,
            stockStatus: isSubtotal ? row.row_label : category.category_label,
            model: isSubtotal ? "" : (row.model_name || row.row_label || "-"),
            modelId: row.model_id || null,
            isTotalRow: false,
            isSubtotalRow: isSubtotal,
          };

          // Add data for each column
          matchedColumns.forEach((col, index) => {
            const colData = row.columns?.[col.id] || {};
            rowData[`col_${index}_total`] = colData.total ?? 0;
            rowData[`col_${index}_price`] =
              colData.price_formatted ||
              formatPriceInCrores(colData.price || 0);
          });

          transformedMatchedData.push(rowData);
        });
      });

      // Add Grand Total row
      if (matchedGrandTotal && Object.keys(matchedGrandTotal).length > 0) {
        const grandTotalData = {
          key: `matched-grand-total-${matchedRowKeyCounter++}`,
          stockStatus: "Total",
          model: "",
          isTotalRow: true,
          isSubtotalRow: false,
        };

        matchedColumns.forEach((col, index) => {
          const colData = matchedGrandTotal[col.id] || {};
          grandTotalData[`col_${index}_total`] = colData.total ?? 0;
          grandTotalData[`col_${index}_price`] =
            colData.price_formatted || formatPriceInCrores(colData.price || 0);
        });

        transformedMatchedData.push(grandTotalData);
      }

      setMatchedStockData(transformedMatchedData);

      // Extract retail_possibility_mtd_report from response
      const retailPossibilityReport =
        response?.data?.data?.retail_possibility_mtd_report || {};
      const retailAreas = retailPossibilityReport?.areas || [];
      const delivered = retailPossibilityReport?.delivered || {};
      const matched = retailPossibilityReport?.matched || {};
      const grandTotal = retailPossibilityReport?.grand_total || {};

      // Generate dynamic columns for Retail Possibility MTD table
      const generatedRetailColumns = [
        {
          title: "Matching",
          dataIndex: "matching",
          key: "matching",
          fixed: "left",
          width: 120,
          onCell: (record, index) => {
            // Calculate rowSpan for Matching column
            if (record.isTotalRow || record.isSubtotalRow) {
              return {};
            }

            const dataSource = transformedRetailData;
            const currentMatching = record.matching;

            // Find how many consecutive rows have the same matching value
            let rowSpan = 1;
            for (let i = index + 1; i < dataSource.length; i++) {
              if (dataSource[i].matching === currentMatching &&
                !dataSource[i].isTotalRow &&
                !dataSource[i].isSubtotalRow) {
                rowSpan++;
              } else {
                break;
              }
            }

            // Check if this is the first row of a group
            if (index === 0 || dataSource[index - 1].matching !== currentMatching ||
              dataSource[index - 1].isTotalRow || dataSource[index - 1].isSubtotalRow) {
              return { rowSpan };
            }

            // Hide subsequent rows in the same group
            return { rowSpan: 0 };
          },
          render: (text, record) => {
            if (record.isTotalRow || record.isSubtotalRow) {
              return <strong>{text}</strong>;
            }
            return text;
          },
        },
        {
          title: "Stock Status",
          dataIndex: "stockStatus",
          key: "stockStatus",
          fixed: "left",
          width: 150,
          onCell: (record, index) => {
            // Calculate rowSpan for Stock Status column within same Matching group
            if (record.isTotalRow || record.isSubtotalRow) {
              return {};
            }

            const dataSource = transformedRetailData;
            const currentMatching = record.matching;
            const currentStockStatus = record.stockStatus;

            // Find how many consecutive rows have the same matching AND stock status
            let rowSpan = 1;
            for (let i = index + 1; i < dataSource.length; i++) {
              if (dataSource[i].matching === currentMatching &&
                dataSource[i].stockStatus === currentStockStatus &&
                !dataSource[i].isTotalRow &&
                !dataSource[i].isSubtotalRow) {
                rowSpan++;
              } else {
                break;
              }
            }

            // Check if this is the first row of a group
            if (index === 0 ||
              dataSource[index - 1].matching !== currentMatching ||
              dataSource[index - 1].stockStatus !== currentStockStatus ||
              dataSource[index - 1].isTotalRow ||
              dataSource[index - 1].isSubtotalRow) {
              return { rowSpan };
            }

            // Hide subsequent rows in the same group
            return { rowSpan: 0 };
          },
          render: (text, record) => {
            if (record.isTotalRow || record.isSubtotalRow) {
              return <strong>{text || ""}</strong>;
            }
            return text || "";
          },
        },
        {
          title: "Model",
          dataIndex: "model",
          key: "model",
          fixed: "left",
          width: 150,
          render: (text, record) => {
            if (record.isTotalRow || record.isSubtotalRow) {
              return <strong>{text || ""}</strong>;
            }
            return text || "";
          },
        },
        ...retailAreas.map((area) => ({
          title: area.name,
          dataIndex: `area_${area.id}`,
          key: `area_${area.id}`,
          align: "center",
          width: 100,
          render: (text, record) => {
            const displayValue = text === 0 || text === "0" ? 0 : text || 0;
            if (record.isTotalRow || record.isSubtotalRow) {
              return <strong>{displayValue}</strong>;
            }
            const retailCategory = String(record.matching || "").toLowerCase();
            if (retailCategory !== "delivered" && retailCategory !== "matched") {
              return displayValue;
            }
            return renderCountLink(displayValue, () =>
              handleStockRedirect(
                record,
                getRetailPossibilityStockCategory(record.matching),
                {
                  stock_status: record.stockStatus,
                  physical_location_names: area.name,
                },
                [area.id]
              )
            );
          },
        })),
        {
          title: "Total",
          dataIndex: "total",
          key: "total",
          align: "center",
          fixed: "right",
          width: 100,
          render: (text, record) => {
            if (record.isTotalRow || record.isSubtotalRow) {
              return <strong>{text}</strong>;
            }
            const retailCategory = String(record.matching || "").toLowerCase();
            if (retailCategory !== "delivered" && retailCategory !== "matched") {
              return text;
            }
            return renderCountLink(text, () =>
              handleStockRedirect(
                record,
                getRetailPossibilityStockCategory(record.matching),
                {
                  stock_status: record.stockStatus,
                }
              )
            );
          },
        },
      ];
      setRetailPossibilityColumns(generatedRetailColumns);

      // Transform Retail Possibility data to table format
      const transformedRetailData = [];
      let retailRowKeyCounter = 1;

      // Process DELIVERED section
      if (delivered && delivered.stock_statuses) {
        delivered.stock_statuses.forEach((stockStatus) => {
          const stockStatusName = stockStatus.stock_status || "";

          // Add model rows for this stock status
          if (stockStatus.models && stockStatus.models.length > 0) {
            stockStatus.models.forEach((model) => {
              const rowData = {
                key: `retail-delivered-${retailRowKeyCounter++}`,
                matching: "Delivered",
                stockStatus: stockStatusName,
                model: model.model_name || "-",
                modelId: model.model_id || model.id || null,
                isTotalRow: false,
                isSubtotalRow: false,
              };

              // Add area counts
              retailAreas.forEach((area) => {
                const areaData = model.areas?.find(
                  (a) => a.area_id === area.id
                );
                rowData[`area_${area.id}`] = areaData?.count || 0;
              });

              rowData.total = model.total || 0;
              transformedRetailData.push(rowData);
            });
          }

          // Add subtotal row for this stock status
          if (stockStatus.subtotal) {
            const subtotalData = {
              key: `retail-delivered-subtotal-${retailRowKeyCounter++}`,
              matching: "Delivered",
              stockStatus: stockStatusName,
              model: "",
              isTotalRow: false,
              isSubtotalRow: true,
            };

            retailAreas.forEach((area) => {
              const areaData = stockStatus.subtotal.areas?.find(
                (a) => a.area_id === area.id
              );
              subtotalData[`area_${area.id}`] = areaData?.count || 0;
            });

            subtotalData.total = stockStatus.subtotal.total || 0;
            transformedRetailData.push(subtotalData);
          }
        });

        // Add delivered total row
        if (delivered.total) {
          const deliveredTotalData = {
            key: `retail-delivered-total-${retailRowKeyCounter++}`,
            matching: "Delivered",
            stockStatus: "Delivered Total",
            model: "",
            isTotalRow: false,
            isSubtotalRow: true,
          };

          retailAreas.forEach((area) => {
            const areaData = delivered.total.areas?.find(
              (a) => a.area_id === area.id
            );
            deliveredTotalData[`area_${area.id}`] = areaData?.count || 0;
          });

          deliveredTotalData.total = delivered.total.total || 0;
          transformedRetailData.push(deliveredTotalData);
        }
      }

      // Process MATCHED section
      if (matched && matched.stock_statuses) {
        matched.stock_statuses.forEach((stockStatus) => {
          const stockStatusName = stockStatus.stock_status || "";

          // Add model rows for this stock status
          if (stockStatus.models && stockStatus.models.length > 0) {
            stockStatus.models.forEach((model) => {
              const rowData = {
                key: `retail-matched-${retailRowKeyCounter++}`,
                matching: "Matched",
                stockStatus: stockStatusName,
                model: model.model_name || "-",
                modelId: model.model_id || model.id || null,
                isTotalRow: false,
                isSubtotalRow: false,
              };

              // Add area counts
              retailAreas.forEach((area) => {
                const areaData = model.areas?.find(
                  (a) => a.area_id === area.id
                );
                rowData[`area_${area.id}`] = areaData?.count || 0;
              });

              rowData.total = model.total || 0;
              transformedRetailData.push(rowData);
            });
          }

          // Add subtotal row for this stock status
          if (stockStatus.subtotal) {
            const subtotalData = {
              key: `retail-matched-subtotal-${retailRowKeyCounter++}`,
              matching: "Matched",
              stockStatus: stockStatusName,
              model: "",
              isTotalRow: false,
              isSubtotalRow: true,
            };

            retailAreas.forEach((area) => {
              const areaData = stockStatus.subtotal.areas?.find(
                (a) => a.area_id === area.id
              );
              subtotalData[`area_${area.id}`] = areaData?.count || 0;
            });

            subtotalData.total = stockStatus.subtotal.total || 0;
            transformedRetailData.push(subtotalData);
          }
        });

        // Add matched total row
        if (matched.total) {
          const matchedTotalData = {
            key: `retail-matched-total-${retailRowKeyCounter++}`,
            matching: "Matched",
            stockStatus: "Matched Total",
            model: "",
            isTotalRow: false,
            isSubtotalRow: true,
          };

          retailAreas.forEach((area) => {
            const areaData = matched.total.areas?.find(
              (a) => a.area_id === area.id
            );
            matchedTotalData[`area_${area.id}`] = areaData?.count || 0;
          });

          matchedTotalData.total = matched.total.total || 0;
          transformedRetailData.push(matchedTotalData);
        }
      }

      // Add Grand Total row
      if (grandTotal) {
        const grandTotalData = {
          key: `retail-grand-total-${retailRowKeyCounter++}`,
          matching: "Grand Count",
          stockStatus: "",
          model: "",
          isTotalRow: true,
          isSubtotalRow: false,
        };

        retailAreas.forEach((area) => {
          const areaData = grandTotal.areas?.find((a) => a.area_id === area.id);
          grandTotalData[`area_${area.id}`] = areaData?.count || 0;
        });

        grandTotalData.total = grandTotal.total || 0;
        transformedRetailData.push(grandTotalData);
      }

      setRetailPossibilityData(transformedRetailData);
    } catch (error) {
      console.error("Error fetching stock details:", error);
      notification.error({
        description:
          error.response?.data?.message ??
          error?.message ??
          "Failed to fetch stock details",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAreaData();
    // Initial fetch with empty array (or default locations if needed)
    fetchStockDetails([], dates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch stock details when physical locations change
  useEffect(() => {
    if (selectedPhysicalLocations.length > 0) {
      fetchStockDetails(selectedPhysicalLocations, dates);
    } else {
      // Fetch all if no selection
      fetchStockDetails([], dates);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPhysicalLocations]);

  const handlePhysicalLocationChange = (values) => {
    setSelectedPhysicalLocations(values || []);
  };

  const handleStartDateChange = (date) => {
    // Ensure end date is not before start date
    if (dates?.endDate?.isBefore(date)) {
      notification.error({
        description: 'Start Date cannot be after End Date.',
      });
      return;
    }

    const newDates = {
      ...dates,
      startDate: date,
    };
    setDates(newDates);
    fetchStockDetails(selectedPhysicalLocations, newDates);
  };

  const handleEndDateChange = (date) => {
    // Ensure start date is not after end date
    if (date?.isBefore(dates.startDate)) {
      notification.error({
        description: 'End Date cannot be before Start Date.',
      });
      return;
    }

    const newDates = {
      ...dates,
      endDate: date,
    };
    setDates(newDates);
    fetchStockDetails(selectedPhysicalLocations, newDates);
  };

  const handleRetailPossibilityExport = async () => {
    try {
      setIsRetailExporting(true);
      const response = await exportRetailPossibilityMtdReportService();

      if (response?.data?.status === 200 && response?.data?.data?.path) {
        const filePath = response.data.data.path;
        const link = document.createElement("a");
        link.href = filePath;
        link.download = filePath?.split("/").pop() || `retail_possibility_mtd_${Date.now()}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }

      notification.error({
        description: "Export file path not found in response",
      });
    } catch (error) {
      notification.error({
        description: error.response?.data?.message ?? error?.message,
      });
    } finally {
      setIsRetailExporting(false);
    }
  };

  const handleStockRedirect = (
    record,
    category,
    additionalParams = {},
    overridePhysicalLocationIds = null
  ) => {
    if (record.isTotalRow) return;
    const allowsNoModel =
      STOCK_REDIRECT_WITHOUT_MODEL.has(category) || record.isUnmatchedTotalRow;
    if (!record.modelId && !allowsNoModel) return;

    const params = new URLSearchParams();
    params.append("stock_dashboard_redirect", "true");
    params.append("stock_dashboard_category", category);
    if (record.modelId) {
      params.append("vehicle_model_id", record.modelId);
      params.append("vehicle_model_name", record.model || "");
    }
    const physicalLocationIdsToUse = Array.isArray(overridePhysicalLocationIds)
      ? overridePhysicalLocationIds
      : selectedPhysicalLocations;

    const selectedLocationNames = physicalLocationIdsToUse
      .map(
        (id) => areaData?.find((area) => String(area.id) === String(id))?.name
      )
      .filter(Boolean)
      .join(", ");
    if (selectedLocationNames) {
      params.append("physical_location_names", selectedLocationNames);
    }
    if (physicalLocationIdsToUse.length > 0) {
      params.append("physical_location_ids", physicalLocationIdsToUse.join(","));
    }
    Object.entries(additionalParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, value);
      }
    });
    physicalLocationIdsToUse.forEach((id) => {
      params.append("physical_location_ids[]", id);
    });

    navigate(`/admin/stock?${params.toString()}`);
  };

  const handleMtdBookingRedirect = (record, areaId = null) => {
    if (record.isTotalRow || !record.modelId) return;

    const params = new URLSearchParams();
    params.append("mtd_booking_redirect", "true");
    params.append("model_id", record.modelId);
    if (areaId) {
      params.append("area_id", areaId);
    }

    navigate(`${route}/leads${exchangeRoute}?${params.toString()}`);
  };

  const handleLiveBookingRedirect = (record, areaId = null, areaName = null) => {
    if (record.isTotalRow || record.isCategoryRow || !record.modelId) return;

    const params = new URLSearchParams();
    params.append("key", "hot_lead");
    params.append("live_booking_redirect", "true");
    params.append(
      "live_booking_category",
      record.liveBookingCategory || ""
    );
    params.append("model_type[0]", record.modelId);
    if (record.rowLabels) {
      params.append("vehicle_model_name", record.rowLabels);
    }
    if (areaId) {
      params.append("area_id[0]", areaId);
      if (areaName) {
        params.append("area_name", areaName);
      }
    }

    navigate(`${route}/leads${exchangeRoute}?${params.toString()}`);
  };

  // // Disable dates that are not in the current month
  // const disabledDate = (current) => {
  //   const today = dayjs();
  //   const isCurrentMonth = 
  //     current && 
  //     current.month() === today.month() && 
  //     current.year() === today.year();
  //   return !isCurrentMonth;
  // };

  // Define columns for Physical Stock Model Wise table
  const physicalStockColumns = [
    {
      title: "Model",
      dataIndex: "model",
      key: "model",
      // fixed: "left",
      width: 200,
      render: (text, record) => {
        if (record.isTotalRow) {
          return <strong>{text}</strong>;
        }
        return text;
      },
    },
    {
      title: "Mddp stock",
      dataIndex: "mddpStock",
      key: "mddpStock",
      align: "center",
      width: 150,
      render: (text, record) =>
        renderStockCountCell(text, record, "mddp_stock"),
    },
    {
      title: "Physical stock",
      dataIndex: "physicalStock",
      key: "physicalStock",
      align: "center",
      width: 150,
      render: (text, record) =>
        renderStockCountCell(text, record, "physical_stock"),
    },
    {
      title: "Transit stock",
      dataIndex: "transitStock",
      key: "transitStock",
      align: "center",
      width: 150,
      render: (text, record) =>
        renderStockCountCell(text, record, "transit_stock"),
    },
  ];

  // Columns for UNMATCHED STOCK ANALYSIS table
  const unmatchedStockColumns = [
    {
      title: "Model ↓",
      dataIndex: "model",
      key: "model",
      fixed: "left",
      width: 180,
      render: (text, record) => {
        if (record.isTotalRow || record.isUnmatchedTotalRow) {
          return <strong>{text}</strong>;
        }
        return text;
      },
    },
    {
      title: "Mddp stock",
      children: [
        {
          title: "Total",
          dataIndex: "mddpTotal",
          key: "mddpTotal",
          align: "center",
          width: 100,
          render: (text, record) =>
            renderStockCountCell(text, record, "unmatched_mddp_stock_total"),
        },
        {
          title: "Price",
          dataIndex: "mddpPrice",
          key: "mddpPrice",
          align: "center",
          width: 100,
          render: (text, record) => {
            if (record.isTotalRow) {
              return <strong>{text}</strong>;
            }
            return text;
          },
        },
      ],
    },
    {
      title: "Physical stock",
      children: [
        {
          title: "Total",
          dataIndex: "physicalTotal",
          key: "physicalTotal",
          align: "center",
          width: 100,
          render: (text, record) =>
            renderStockCountCell(text, record, "unmatched_physical_stock_total"),
        },
        {
          title: "Price",
          dataIndex: "physicalPrice",
          key: "physicalPrice",
          align: "center",
          width: 100,
          render: (text, record) => {
            if (record.isTotalRow) {
              return <strong>{text}</strong>;
            }
            return text;
          },
        },
      ],
    },
    {
      title: "Transit stock",
      children: [
        {
          title: "Total",
          dataIndex: "transitTotal",
          key: "transitTotal",
          align: "center",
          width: 100,
          render: (text, record) =>
            renderStockCountCell(text, record, "unmatched_transit_stock_total"),
        },
        {
          title: "Price",
          dataIndex: "transitPrice",
          key: "transitPrice",
          align: "center",
          width: 100,
          render: (text, record) => {
            if (record.isTotalRow) {
              return <strong>{text}</strong>;
            }
            return text;
          },
        },
      ],
    },
    {
      title: "Summary",
      children: [
        {
          title: "Total",
          dataIndex: "summaryTotal",
          key: "summaryTotal",
          align: "center",
          width: 100,
          render: (text, record) =>
            renderStockCountCell(text, record, "unmatched_summary_total"),
        },
        {
          title: "Price",
          dataIndex: "summaryPrice",
          key: "summaryPrice",
          align: "center",
          width: 100,
          render: (text, record) => {
            if (record.isTotalRow) {
              return <strong>{text}</strong>;
            }
            return text;
          },
        },
      ],
    },
  ];

  // Columns for Refund TAT Report table
  const refundTatColumns = [
    {
      title: "Lead ID",
      dataIndex: "leadId",
      key: "leadId",
      align: "center",
      width: 100,
    },
    {
      title: "Customer Name",
      dataIndex: "customerName",
      key: "customerName",
      align: "center",
      width: 150,
    },
    {
      title: "Area",
      dataIndex: "area",
      key: "area",
      align: "center",
      width: 120,
    },
    {
      title: "Cancellation Request Creation Date",
      dataIndex: "cancellationRequestCreationDate",
      key: "cancellationRequestCreationDate",
      align: "center",
      width: 220,
      render: (text) => {
        if (!text || text === "-") return "-";
        try {
          return dayjs(text).format("DD/MM/YYYY");
        } catch (error) {
          return text;
        }
      },
    },
    {
      title: "Cancellation Request Approval Date",
      dataIndex: "cancellationRequestApprovalDate",
      key: "cancellationRequestApprovalDate",
      align: "center",
      width: 220,
      render: (text) => {
        if (!text || text === "-") return "-";
        try {
          return dayjs(text).format("DD/MM/YYYY");
        } catch (error) {
          return text;
        }
      },
    },
    {
      title: "Refund Transaction Entry Date",
      dataIndex: "refundTransactionEntryDate",
      key: "refundTransactionEntryDate",
      align: "center",
      width: 220,
      render: (text) => {
        if (!text || text === "-") return "-";
        try {
          return dayjs(text).format("DD/MM/YYYY");
        } catch (error) {
          return text;
        }
      },
    },
    {
      title: "Total TAT (Days)",
      dataIndex: "totalTat",
      key: "totalTat",
      align: "center",
      width: 150,
      render: (text) => {
        if (
          text === "-" ||
          text === "" ||
          text === null ||
          text === undefined
        ) {
          return "-";
        }
        // Highlight values 3, 4, and 7 with light red background
        const numericValue =
          typeof text === "number" ? text : parseInt(text, 10);
        const highlightValues = [3, 4, 7];
        const shouldHighlight =
          !isNaN(numericValue) && highlightValues.includes(numericValue);
        return (
          <span
            style={{
              backgroundColor: shouldHighlight ? "#ffcccc" : "transparent",
              padding: "2px 8px",
              borderRadius: "4px",
            }}
          >
            {text}
          </span>
        );
      },
    },
  ];

  // Custom row rendering for styling
  const getRowClassName = (record) => {
    // Handle LIVE BOOKING table rows
    if (record.isTotalRow) {
      return "total-row"; // Grand Count row
    }
    if (record.isCategoryRow) {
      return "section-header-row"; // Matched/Unmatched category headers
    }
    // Handle MATCHED STOCK ANALYSIS table rows
    if (record.isSubtotalRow) {
      return "section-header-row"; // Subtotal rows for matched stock and retail possibility
    }
    // Handle Retail Possibility MTD table rows
    if (record.key?.startsWith("retail-")) {
      if (record.isTotalRow) {
        return "total-row"; // Grand Count row
      }
      if (record.isSubtotalRow) {
        return "section-header-row"; // Subtotal rows
      }
    }
    // Handle legacy keys (for backward compatibility)
    if (record.key === "4" || record.key === "10") {
      return "total-row";
    }
    if (
      record.key === "3" ||
      record.key === "11" ||
      record.key === "15" ||
      record.key === "16"
    ) {
      return "total-row";
    }
    if (
      record.key === "6" ||
      record.key === "7" ||
      record.key === "8" ||
      record.key === "11" ||
      record.key === "19" ||
      record.key === "23" ||
      record.key === "24" ||
      record.key === "25"
    ) {
      return "total-row";
    }
    if (record.key === "1" || record.key === "7") {
      return "section-header-row";
    }
    return "";
  };

  return (
    <>
      <Loader loading={loading} />
      <div className="user-data">
        <div style={{ padding: "20px 0" }}>
          <Title level={4}>PHYSICAL STOCK ANALYSIS</Title>

          {/* Physical Location Filter */}

          <Row gutter={[16, 16]} className="mb-4 mt-4">
            {/* Model Dropdown */}
            <Col xl={6} lg={12} md={12} sm={24} xs={24}>
              <Form layout="vertical">
                <Form.Item label="Physical Location" style={{ marginBottom: 0 }}>
                  <Select
                    mode="multiple"
                    placeholder="Select Physical Location(s)"
                    allowClear
                    value={selectedPhysicalLocations}
                    onChange={handlePhysicalLocationChange}
                    className="form-control-ant"
                    loading={loading}
                  >
                    {areaData?.map((area) => (
                      <Select.Option key={area.id} value={area.id}>
                        {area.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Form>
            </Col>
          </Row>

          {/* Physical Stock Model Wise Table */}
          <div style={{ marginBottom: "30px" }}>
           
            <div className="dashboard-table-container">
              <Table
                columns={physicalStockColumns}
                dataSource={physicalStockData}
                pagination={false}
                bordered
                scroll={{ x: "max-content", y: 400 }}
                rowClassName={(record) => {
                  if (record.isTotalRow) {
                    return "total-row";
                  }
                  return "";
                }}
                size="middle"
              />
            </div>
          </div>

          {/* UNMATCHED STOCK ANALYSIS Table */}
          <div style={{ marginBottom: "30px" }}>
            <Title level={5} style={{ marginBottom: "15px" }}>
              UNMATCHED STOCK ANALYSIS
            </Title>
            <div className="dashboard-table-container">
              <Table
                columns={unmatchedStockColumns}
                dataSource={unmatchedStockData}
                pagination={false}
                bordered
                scroll={{ x: "max-content", y: 400 }}
                rowClassName={(record) => {
                  if (record.isTotalRow || record.isUnmatchedTotalRow) {
                    return "total-row";
                  }
                  return "";
                }}
                size="middle"
              />
            </div>
          </div>

          {/* MATCHED STOCK ANALYSIS Table */}
          <div style={{ marginBottom: "30px" }}>
            <Title level={5} style={{ marginBottom: "15px" }}>
              MATCHED STOCK ANALYSIS
            </Title>
            <div className="dashboard-table-container">
              <Table
                columns={matchedStockColumns}
                dataSource={matchedStockData}
                pagination={false}
                bordered
                scroll={{ x: "max-content", y: 400 }}
                rowClassName={getRowClassName}
                size="middle"
              />
            </div>
          </div>

          {/* Retail Possibility MTD Table */}
          <div style={{ marginBottom: "30px" }}>
            <div
              style={{
                marginBottom: "15px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Title level={5} style={{ marginBottom: 0 }}>
                Retail Possibility MTD
              </Title>
              <Button
                type="primary"
                className="secondary-button reports-download-button"
                icon={<DownloadOutlined />}
                onClick={handleRetailPossibilityExport}
                loading={isRetailExporting}
              >
                Download
              </Button>
            </div>
            <div className="dashboard-table-container">
              <Table
                columns={retailPossibilityColumns}
                dataSource={retailPossibilityData}
                pagination={false}
                bordered
                scroll={{ x: "max-content", y: 400 }}
                rowClassName={getRowClassName}
                size="middle"
              />
            </div>
          </div>

          {/* MTD Booking Table */}
          <div style={{ marginBottom: "30px" }}>
            <Title level={5} style={{ marginBottom: "15px" }}>
              MTD Booking
            </Title>
            <div className="dashboard-table-container">
              <Table
                columns={mtdBookingColumns}
                dataSource={mtdBookingData}
                pagination={false}
                bordered
                scroll={{ x: "max-content", y: 400 }}
                rowClassName={getRowClassName}
                size="middle"
              />
            </div>
          </div>

          {/* LIVE BOOKING Table */}
          <div style={{ marginBottom: "30px" }}>
            <Title level={5} style={{ marginBottom: "15px" }}>
              LIVE BOOKING
            </Title>
            <div className="dashboard-table-container">
              <Table
                columns={liveBookingColumns}
                dataSource={liveBookingData}
                pagination={false}
                bordered
                scroll={{ x: "max-content", y: 400 }}
                rowClassName={getRowClassName}
                size="middle"
              />
            </div>
          </div>

          {/* Cancellation Report and Refund TAT Report Section */}
          <Row gutter={[16, 16]} className="mb-3">
            <Col xl={6} lg={12} md={12} sm={24} xs={24}>
              <Form layout="vertical">
                <Form.Item label="From Date" style={{ marginBottom: 0 }}>
                  <DatePicker
                    format="DD/MM/YYYY"
                    onChange={handleStartDateChange}
                    value={dates.startDate}
                    placeholder="From Date"
                    // disabledDate={disabledDate}
                    defaultValue={currentMonthStart}
                    allowClear={false}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Form>
            </Col>
            <Col xl={6} lg={12} md={12} sm={24} xs={24}>
              <Form layout="vertical">
                <Form.Item label="To Date" style={{ marginBottom: 0 }}>
                  <DatePicker
                    format="DD/MM/YYYY"
                    onChange={handleEndDateChange}
                    value={dates.endDate}
                    placeholder="To Date"
                    // disabledDate={disabledDate}
                    defaultValue={currentDate}
                    allowClear={false}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Form>
            </Col>
          </Row>
          <Row gutter={[24, 24]} style={{ marginBottom: "30px" }}>
            <Col xl={8} lg={8} md={24} sm={24} xs={24}>
              <Card>
                <Title level={5} style={{ marginBottom: "15px" }}>
                  Cancellation Report
                </Title>
                <PieChart
                  data={cancellationReportData}
                  chartColors={cancellationChartColors}
                />
              </Card>
            </Col>
            <Col xl={16} lg={16} md={24} sm={24} xs={24}>
              <Card>
                <Title level={5} style={{ marginBottom: "15px" }}>
                  Refund TAT Report
                </Title>
                <div className="dashboard-table-container">
                  <Table
                    columns={refundTatColumns}
                    dataSource={refundTatData}
                    pagination={false}
                    bordered
                    scroll={{ x: "max-content", y: 400 }}
                    loading={loading}
                    size="middle"
                  />
                </div>
              </Card>
            </Col>
          </Row>
        </div>

        <style>{`
        .total-row {
          font-weight: bold;
          background-color: #f5f5f5;
          position: sticky;
          bottom: 0;
          z-index: 10;
        }
        .total-row td {
          background-color: #f5f5f5 !important;
        }
        .section-header-row {
          font-weight: bold;
          background-color: #e3f2fd;
        }
        .ant-table-thead > tr > th {
          background-color: #fafafa;
        }
        .ant-table-body {
          position: relative;
        }
        .ant-table-tbody > tr.total-row {
          position: sticky;
          bottom: 0;
          z-index: 10;
        }
        .ant-table-tbody > tr.total-row > td {
          background-color: #f5f5f5 !important;
          border-bottom: 1px solid #f0f0f0;
        }
      `}</style>
      </div>
    </>
  );
};

export default DashboardStock;
